import React from "react";
import styled, { css } from "styled-components";

import ToolTip from "../tooltip/ToolTip";
import { getFileIconFromExtension } from "../file-icon/fileIcon";

/**
 * 添付ファイルカード（枠 + 種別アイコン + ファイル名 + ダウンロードボタン）。
 *
 * 元はトークルームのメッセージ添付（MessageItem）のプレビュー不可ファイル用
 * UI だったものを共通化した。メッセージ添付と共有ノート添付で同じ見た目を
 * 保証するため、スタイルの一次定義はこのファイルに置く。
 *
 * 2 つの描画モードを持つ:
 * - 通常（div）: ダウンロードは右端の <button>（onDownload）。
 *   `downloadDisabled` でグレーアウトし、`downloadDisabledReason` を
 *   ツールチップで出す（MessageItem の従来挙動）。
 * - アンカー（`href` 指定時）: カード全体を <a> として描画する。Markdown の
 *   段落（<p>）内でも valid な HTML になるよう、内側にボタンは置かず
 *   ダウンロードアイコンは装飾（span）にする。クリック処理は onClick で
 *   呼び出し側が行う（preventDefault + downloadFile 等）。
 *   `onDownloadIconClick` を渡すとアイコンだけ独立したクリック領域になる
 *   （本体クリック = プレビュー、アイコン = ダウンロードの出し分け用。
 *   ハンドラ側で preventDefault / stopPropagation すること）。
 */

// ダウンロードボタン
// URL が無い場合は $disabled で無効表示にする（native disabled にすると
// hover イベントが発火せずツールチップを出せないため transient prop で制御）
const DownloadButton = styled.button<{ $disabled?: boolean }>`
  background: transparent;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;

  .material-symbols-rounded {
    font-size: 24px;
    color: ${({ $disabled }) =>
      $disabled ? "var(--soremo-border)" : "var(--soremo-light-gray)"};
  }

  &:hover .material-symbols-rounded {
    color: ${({ $disabled }) =>
      $disabled ? "var(--soremo-border)" : "var(--soremo-blue)"};
  }
`;

// カード枠
const CardRoot = styled.div.attrs({
  className: "bodytext-11 u-line-height-100",
})<{ $link?: boolean }>`
  padding: 4px;
  background-color: var(--soremo-background);
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  display: flex;
  flex-direction: column;

  ${({ $link }) =>
    $link &&
    css`
      /* Markdown コンテナの & a（soremo-blue / 下線）より強くするため && */
      && {
        color: inherit;
        text-decoration: none;
        cursor: pointer;
      }

      /* カード全体がリンクなので、カードのどこをホバーしても DL アイコンを強調 */
      @media (hover: hover) {
        &:hover ${DownloadButton} .material-symbols-rounded {
          color: var(--soremo-blue);
        }
      }
    `}
`;

// ファイル名とアクション
// アンカーモードでは <p> > <a> 配下に入るため、valid な HTML になるよう
// div ではなく span（display: flex）で組む。
const CardHeader = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  padding: 4px 4px 0px 4px;
`;

// ファイルタイプアイコン
const FileTypeIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 20px;
  color: var(--soremo-light-gray);
  flex-shrink: 0;
`;

// ファイル名
const FileName = styled.span.attrs({
  className: "bodytext-11 u-line-height-150",
})`
  word-break: break-all;
  flex: 1;
`;

export interface AttachmentFileCardProps {
  /** 表示するファイル名 */
  fileName: string;
  /**
   * 先頭の種別アイコン（Material Symbols 名）。
   * 未指定なら fileName の拡張子から解決する。
   */
  icon?: string;
  /** ダウンロードボタン押下時（通常モードのみ） */
  onDownload?: () => void;
  /** ダウンロード不可のときボタンをグレーアウトする（通常モードのみ） */
  downloadDisabled?: boolean;
  /** ダウンロード不可時にボタンへ出すツールチップ（通常モードのみ） */
  downloadDisabledReason?: string;
  /** 指定するとカード全体を <a> として描画する（アンカーモード） */
  href?: string;
  /** アンカーモードのクリック処理（preventDefault してダウンロード等） */
  // eslint-disable-next-line no-unused-vars
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * アンカーモードでダウンロードアイコンを独立したクリック領域にする。
   * 未指定ならアイコンは装飾のまま（従来挙動）。
   */
  // eslint-disable-next-line no-unused-vars
  onDownloadIconClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  /** カード下部に挿し込む要素（ダウンロード既読アバター等） */
  children?: React.ReactNode;
  className?: string;
}

const AttachmentFileCard: React.FC<AttachmentFileCardProps> = ({
  fileName,
  icon,
  onDownload,
  downloadDisabled = false,
  downloadDisabledReason,
  href,
  onClick,
  onDownloadIconClick,
  children,
  className,
}) => {
  const resolvedIcon = icon ?? getFileIconFromExtension(fileName);

  if (href !== undefined) {
    return (
      <CardRoot
        as="a"
        href={href}
        onClick={onClick}
        $link
        className={className}
      >
        <CardHeader>
          <FileTypeIcon>{resolvedIcon}</FileTypeIcon>
          <FileName>{fileName}</FileName>
          {onDownloadIconClick ? (
            // 本体クリック（プレビュー等）と分離した直接ダウンロード
            <DownloadButton
              as="span"
              role="button"
              aria-label="ダウンロード"
              onClick={onDownloadIconClick}
            >
              <span className="material-symbols-rounded">download</span>
            </DownloadButton>
          ) : (
            // カード自体がリンクのため、アイコンは装飾（クリック領域を分けない）
            <DownloadButton as="span" aria-hidden="true">
              <span className="material-symbols-rounded">download</span>
            </DownloadButton>
          )}
        </CardHeader>
        {children}
      </CardRoot>
    );
  }

  return (
    <CardRoot className={className}>
      <CardHeader>
        <FileTypeIcon>{resolvedIcon}</FileTypeIcon>
        <FileName>{fileName}</FileName>
        <ToolTip
          description={downloadDisabled ? (downloadDisabledReason ?? "") : ""}
          placement="top"
          appendTo="parent"
        >
          <DownloadButton
            $disabled={downloadDisabled}
            aria-disabled={downloadDisabled}
            onClick={onDownload}
          >
            <span className="material-symbols-rounded">download</span>
          </DownloadButton>
        </ToolTip>
      </CardHeader>
      {children}
    </CardRoot>
  );
};

export default AttachmentFileCard;
